import './index.scss'
import React, { useEffect, useRef, useState } from 'react'
import { Button, TextField, Typography } from '@material-ui/core'
import { withRouter } from 'react-router-dom'
import validate from 'validate.js'
import { setToken, setAdminId, setAdminName } from 'api/ApiClient'
import { loginPromise, verifyLoginCodePromise, resendLoginCodePromise } from 'api/Auth'
import { intiAdminPermission } from 'helpers/permission'
import PasscodeFields from './PasscodeFields'
import { boxesToCode, digitsToBoxes, isComplete } from './passcode'
import {
  CREDENTIALS,
  PASSCODE,
  envelopeCodeFrom,
  loginFailureAction,
  messageFrom,
  nextStepFromLogin,
  resendFailureAction,
  resendNotice,
  statusFrom,
  verifyFailureAction,
} from './loginFlow'

const schema = {
  username: {
    presence: { allowEmpty: false, message: 'is required' },
  },
  password: {
    presence: { allowEmpty: false, message: 'is required' },
  },
}

const EMPTY_BOXES = digitsToBoxes('')

const Login = props => {
  const { history } = props

  const [formState, setFormState] = useState({
    isValid: false,
    values: {},
    touched: {},
    errors: {},
  })

  const [step, setStep] = useState(CREDENTIALS)
  // The challengeId lives in component state, never in the URL: a URL is copied into tickets, logged
  // by proxies and left in browser history.
  const [challenge, setChallenge] = useState({ id: null, email: '' })
  const [boxes, setBoxes] = useState(EMPTY_BOXES)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [busy, setBusy] = useState(false)

  // completeSignIn navigates away, which unmounts this component synchronously -- React 16 does not
  // batch across an await, so a `finally { setBusy(false) }` afterwards would fire on a dead
  // component and log "Can't perform a React state update on an unmounted component" on every
  // successful sign-in.
  const mounted = useRef(true)
  useEffect(() => () => { mounted.current = false }, [])
  const settle = () => { if (mounted.current) setBusy(false) }

  useEffect(() => {
    const errors = validate(formState.values, schema)

    setFormState(formState => ({
      ...formState,
      isValid: errors ? false : true,
      errors: errors || {},
    }))
  }, [formState.values])

  const handleChange = event => {
    event.persist()

    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        [event.target.name]:
          event.target.type === 'checkbox'
            ? event.target.checked
            : event.target.value,
      },
      touched: {
        ...formState.touched,
        [event.target.name]: true,
      },
    }))
  }

  const completeSignIn = response => {
    setToken(response.token)
    setAdminId(response.id)
    setAdminName(response.email)
    intiAdminPermission(response.id)
    history.push('/users')
  }

  const handleLogin = async event => {
    event.preventDefault()
    setError('')
    setNotice('')
    setBusy(true)

    try {
      const response = await loginPromise(
        formState.values.username,
        formState.values.password
      )
      const next = nextStepFromLogin(response)

      if (next.signIn) {
        // admin_mfa_required is off -- single-step login, unchanged.
        completeSignIn(next.signIn)
        return
      }

      if (next.step === PASSCODE) {
        setChallenge({ id: next.challengeId, email: formState.values.username })
        setBoxes(EMPTY_BOXES)
        // Clear `busy` BEFORE switching screens. React 16 renders each of these synchronously and
        // separately, so mounting PasscodeFields while busy is still true mounts its first input
        // `disabled` -- and React applies autoFocus by calling .focus() once on mount, which a
        // disabled element ignores. The admin would land on the code screen with nothing focused.
        setBusy(false)
        setStep(PASSCODE)
        return
      }

      setError(next.error)
    } catch (err) {
      // A failed passcode send comes back as a real error rather than a challenge, because there is
      // no second delivery path -- advancing to the code screen would leave the admin waiting for an
      // email that is never coming. Show it here with a way forward instead of only a toast.
      setError(loginFailureAction(statusFrom(err), messageFrom(err, null), envelopeCodeFrom(err)).error)
    } finally {
      settle()
    }
  }

  const handleVerify = async event => {
    event.preventDefault()
    setError('')
    setNotice('')
    setBusy(true)

    try {
      const response = await verifyLoginCodePromise(challenge.id, boxesToCode(boxes))
      if (response && response.token) {
        completeSignIn(response)
        return
      }
      setError('We could not sign you in. Please try again.')
    } catch (err) {
      // Branching on the status, never on the copy: the wording is server-side and translated, so a
      // string match would stop working silently.
      const action = verifyFailureAction(statusFrom(err), messageFrom(err, null))
      setBoxes(EMPTY_BOXES)
      if (action.clearChallenge) setChallenge({ id: null, email: '' })
      setStep(action.step)
      setError(action.error)
    } finally {
      settle()
    }
  }

  const handleResend = async () => {
    setError('')
    setNotice('')
    setBusy(true)

    try {
      const response = await resendLoginCodePromise(challenge.id)
      setBoxes(EMPTY_BOXES)
      // The server keeps the same challengeId on a resend, but take whatever it returns rather than
      // assuming: if that ever changes, holding the old id would fail every subsequent verify and
      // tell the admin their correct code was wrong.
      if (response && response.challengeId && response.challengeId !== challenge.id) {
        setChallenge(current => ({ ...current, id: response.challengeId }))
      }
      // Deliberately does not claim an email went out. A 200 from /login/resend does not mean one
      // did: the API answers 200 for an expired or already-used challenge, and for the decoy a
      // non-admin gets, so that a resend cannot be used to ask "is this address an admin?". Saying
      // "We sent a new code" is what turns an expired challenge into a loop -- the admin waits for
      // mail that is not coming, and the only real exit reads as optional.
      setNotice(resendNotice())
    } catch (err) {
      // A 401 here means the challenge is gone -- expired or already used. Staying on this screen
      // would be a dead end: the code no longer works and Resend, the only control that looks like
      // it might help, cannot help either.
      const action = resendFailureAction(statusFrom(err), messageFrom(err, null))
      if (action.clearChallenge) setChallenge({ id: null, email: '' })
      setStep(action.step)
      setError(action.error)
    } finally {
      settle()
    }
  }

  // Back to step 1 without a page reload: a typo in the email address is the most common reason to
  // need this, and window.location would throw away everything already typed and burn the challenge.
  const handleBackToSignIn = event => {
    event.preventDefault()
    setStep(CREDENTIALS)
    setChallenge({ id: null, email: '' })
    setBoxes(EMPTY_BOXES)
    setError('')
    setNotice('')
  }

  const hasError = field =>
    formState.touched[field] && formState.errors[field] ? true : false

  const renderMessages = () => (
    <React.Fragment>
      {error ? (
        <Typography className={'form-error'} color="error" variant="body2" role="alert">
          {error}
        </Typography>
      ) : null}
      {notice ? (
        <Typography className={'form-notice'} variant="body2" role="status">
          {notice}
        </Typography>
      ) : null}
    </React.Fragment>
  )

  if (step === PASSCODE) {
    return (
      <div className={'page-background'}>
        <div className={'content-body'}>
          <form className={'form'} onSubmit={handleVerify}>
            <Typography variant="h5">Enter the code we emailed you</Typography>
            {/* Admins have more than one account and more than one inbox. Without this, the first
                failure mode is checking the wrong mailbox and assuming the system is broken. */}
            <Typography className={'passcode-sent-to'} variant="body2">
              We sent a 6-digit code to <strong>{challenge.email}</strong>. It expires in 10 minutes.
              {' '}Wrong address, or nothing arriving? Go back to sign in and start again.
            </Typography>

            <PasscodeFields
              boxes={boxes}
              onChange={setBoxes}
              disabled={busy}
              autoFocus
            />

            {renderMessages()}

            <Button
              className={'sign-in'}
              color="primary"
              disabled={busy || !isComplete(boxes)}
              fullWidth
              size="large"
              type="submit"
              variant="contained">
              Verify and sign in
            </Button>

            <div className={'passcode-actions'}>
              <Button color="primary" disabled={busy} onClick={handleResend} size="small" type="button">
                Resend code
              </Button>
              <Button color="primary" disabled={busy} onClick={handleBackToSignIn} size="small" type="button">
                Back to sign in
              </Button>
            </div>
          </form>
        </div>
      </div>
    )
  }

  return (
    <div className={'page-background'}>
      <div className={'content-body'}>
        <form className={'form'} onSubmit={handleLogin}>
          <TextField
            className={'text-field'}
            fullWidth
            label="Username"
            name="username"
            onChange={handleChange}
            type="text"
            value={formState.values.username || ''}
            variant="outlined"
          />
          <TextField
            className={'text-field'}
            error={hasError('password')}
            fullWidth
            helperText={
              hasError('password') ? formState.errors.password[0] : null
            }
            label="Password"
            name="password"
            onChange={handleChange}
            type="password"
            value={formState.values.password || ''}
            variant="outlined"
          />

          {renderMessages()}

          <Button
            className={'sign-in'}
            color="primary"
            disabled={busy || !formState.isValid}
            fullWidth
            size="large"
            type="submit"
            variant="contained">
            Sign in now
          </Button>
        </form>
      </div>
    </div>
  )
}

export default withRouter(Login)
